/** @file Rubric store module, handling the loading and updating of rubrics with the server. */
import Vue from 'vue';

import * as api from '@codegrade/api';

import client from '@/client';
import { NONEXISTENT } from '@/constants';
import * as models from '@/models';
import { Rubric, RubricResult } from '@/models/rubric';
import * as utils from '@/utils';

import { store } from '..';
import * as types from '../mutation-types';
import { AssignmentsStore } from './assignments';
import { SubmissionsStore } from './submissions';

const getters = {
    rubrics: state => state.rubrics,
    results: state => state.results,
};

const loaders = {
    rubrics: {},
    results: {},
};

const actions = {
    /**
     * Action to load rubrics for an assignment.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID and force flag.
     * @param payload.assignmentId The ID of the assignment whose rubric needs to be loaded.
     * @param payload.force When true, force reload the rubric, even if it is already loaded.
     * @returns A promise that will resolve with the API result.
     */
    async loadRubric({ commit, state }, { assignmentId, force }) {
        if (state.rubrics[assignmentId] && !force) {
            return state.rubrics[assignmentId];
        }
        let loader = loaders.rubrics[assignmentId];
        if (loader == null) {
            loader = client.p.assignment
                .getRubric({ assignmentId })
                .then(
                    response => response.cgResult,
                    caught => {
                        // In case the rubric doesn't exist silence the error.
                        if (
                            caught instanceof api.ApiResultError &&
                            caught.response.status === 404
                        ) {
                            return NONEXISTENT;
                        } else {
                            throw caught;
                        }
                    },
                )
                .then(rubric => {
                    commit(types.SET_RUBRIC, { assignmentId, rubric });
                })
                .finally(() => {
                    delete loaders.rubrics[assignmentId];
                });
            loaders.rubrics[assignmentId] = loader;
        }

        return await loader;
    },

    /**
     * Copies a rubric from one assignment to another.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the source and destination assignments.
     * @param payload.fromAssignment The ID of the assignment to copy the rubric from.
     * @param payload.intoAssignment The ID of the assignment to copy the rubric into.
     * @returns A promise that will resolve with the API result.
     */
    async copyRubric({ commit }, { fromAssignment, intoAssignment }) {
        const response = await client.p.assignment.copyRubric({
            assignmentId: intoAssignment.id,
            requestBody: { oldAssignmentId: fromAssignment.id },
        });
        await AssignmentsStore.loadSingleAssignment({
            assignmentId: intoAssignment.id,
            courseId: intoAssignment.courseId,
            force: true,
        });
        commit(types.SET_RUBRIC, {
            assignmentId: intoAssignment.id,
            rubric: response.cgResult,
        });
        return response;
    },

    /**
     * Updates the rubric in the Vuex store for a specific assignment.
     *
     * @param context - Vuex context object.
     * @param payload - Payload containing the assignment ID and rubric data.
     * @param payload.assignmentId - The ID of the assignment whose rubric is being updated.
     * @param payload.rubric - The rubric data to update in the store.
     */
    updateRubricInStore({ commit }, { assignmentId, rubric }) {
        commit(types.SET_RUBRIC, {
            assignmentId,
            rubric,
        });
    },

    /**
     * Updates the rubric for a specific assignment.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID, rubric data, and maximum points.
     * @param payload.assignmentId The ID of the assignment whose rubric is being updated.
     * @param payload.rubric The rubric data to update.
     * @param payload.maxPoints The maximum points for the updated rubric.
     * @returns A promise that resolves with the API result.
     */
    async updateRubric({ commit }, { assignmentId, rubric, maxPoints }) {
        const response = await client.p.assignment.putRubric({
            assignmentId,
            requestBody: {
                rows: rubric.rows,
                maxPoints,
            },
        });
        await AssignmentsStore.loadSingleAssignment({
            assignmentId,
            force: true,
        });
        commit(types.SET_RUBRIC, {
            assignmentId,
            rubric: rubric.updateFromApiServerData(response.cgResult),
        });
        return response;
    },

    /**
     * Deletes the rubric for a specific assignment.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID.
     * @param payload.assignmentId The ID of the assignment whose rubric is being deleted.
     * @returns A promise that resolves with the api result.
     */
    async deleteRubric({ commit }, { assignmentId }) {
        const response = await client.p.assignment.deleteRubric({ assignmentId });
        await AssignmentsStore.loadSingleAssignment({
            assignmentId,
            force: true,
        });
        return response.addCallbacks({
            onAfterSuccess: () => {
                commit(types.SET_RUBRIC, {
                    assignmentId,
                    rubric: NONEXISTENT,
                });
            },
        });
    },

    /**
     * Clears the rubric for a specific assignment from the Vuex store.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID.
     * @param payload.assignmentId The ID of the assignment whose rubric is being cleared.
     */
    clearRubric({ commit, state }, { assignmentId }) {
        if (Object.hasOwnProperty.call(state.rubrics, assignmentId)) {
            commit(types.CLEAR_RUBRIC, { assignmentId });
        }
    },

    /**
     * Loads the result for a specific assignment submission.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID, submission ID, and force flag.
     * @param payload.assignmentId The ID of the assignment whose result is being loaded.
     * @param payload.submissionId The ID of the submission whose result is being loaded.
     * @param payload.force Whether to force reload the result, even if it is already loaded.
     * @returns A promise that resolves with the API result.
     */
    async loadResult({ commit, state }, { assignmentId, submissionId, force }) {
        if (state.results[submissionId] && !force) {
            return Promise.resolve();
        }

        if (!Object.hasOwnProperty.call(loaders.results, submissionId)) {
            let response;
            try {
                response = await SubmissionsStore.loadSingleSubmission({
                    assignmentId,
                    submissionId,
                });
            } catch (caught) {
                utils.vueDelete(loaders.results, submissionId);
                commit(types.SET_RUBRIC_RESULT, {
                    submissionId,
                    result: NONEXISTENT,
                });
                throw caught;
            }
            utils.vueDelete(loaders.results, submissionId);
            response.cgResult.rubricResult
                .filter(r => r.rubric.rows.length !== 0)
                .caseOf({
                    Just: result => {
                        commit(types.SET_RUBRIC_RESULT, {
                            submissionId,
                            result,
                        });
                    },
                    Nothing: () => {
                        commit(types.SET_RUBRIC_RESULT, {
                            submissionId,
                            result: NONEXISTENT,
                        });
                    },
                });
            loaders.results[submissionId] = response;
        }

        return loaders.results[submissionId];
    },

    /**
     * Clears the result for a specific submission or multiple submissions from the store.
     *
     * @param context Vuex context object.
     * @param payload Payload containing either the submission ID or an array of submission IDs.
     * @param payload.submissionId Optional ID of the submission whose result should be cleared.
     * @param payload.submissionIds Optional list of IDs whose results should be cleared.
     */
    clearResult({ commit, state }, { submissionId, submissionIds }) {
        let ids = submissionIds;
        if (submissionIds == null) {
            ids = [submissionId];
        }
        ids.forEach(id => {
            if (Object.hasOwnProperty.call(state.results, id)) {
                commit(types.CLEAR_RUBRIC_RESULT, { submissionId: id });
            }
        });
    },

    /**
     * Updates the rubric of an assignment submission.
     *
     * @param context Vuex context object.
     * @param payload Payload containing the assignment ID, submission ID, and result data.
     * @param payload.assignmentId The ID of the assignment associated with the rubric result.
     * @param payload.submissionId The ID of the submission whose rubric result is being updated.
     * @param payload.result The updated rubric result for the submission.
     * @returns A promise resolves with the API result.
     */
    async updateRubricResult({ commit, state, dispatch }, { assignmentId, submissionId, result }) {
        await Promise.all([
            SubmissionsStore.loadSingleSubmission({ assignmentId, submissionId }),
            dispatch('loadRubric', { assignmentId }),
        ]);

        const rubric = state.rubrics[assignmentId];
        result.validate().throwOnError();

        const selected = { ...result.selected };
        rubric.rows.forEach(row => {
            if (row.locked) {
                delete selected[row.id];
            }
        });

        const response = await client.p.submission.patchRubricResult({
            submissionId,
            copyLockedItems: true,
            requestBody: {
                items: Object.entries(selected).map(([rowId, item]) => ({
                    rowId: Number(rowId),
                    itemId: item.id,
                    multiplier: item.multiplier,
                })),
            },
        });

        return response.addCallbacks({
            onSuccess: ({ cgResult }) => {
                SubmissionsStore.commitSingleSubmission({
                    submission: models.Submission.fromApiServerData(cgResult),
                });
                cgResult.rubricResult.ifJust(rubricResult =>
                    commit(types.SET_RUBRIC_RESULT, {
                        submissionId,
                        assignmentId,
                        result: RubricResult.fromApiServerData(submissionId, rubricResult),
                    }),
                );
            },
        });
    },
};

// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const mutations = {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_RUBRIC](state, { assignmentId, rubric }) {
        let rubricModel = rubric;
        if (Array.isArray(rubric) && rubric.every(row => row instanceof api.RubricRowBase)) {
            rubricModel = Rubric.fromApiServerData(rubric);
        } else if (rubricModel !== NONEXISTENT && !(rubric instanceof Rubric)) {
            rubricModel = Rubric.fromServerData(rubric);
        }
        Vue.set(state.rubrics, assignmentId, rubricModel);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_RUBRIC](state, { assignmentId }) {
        Vue.delete(state.rubrics, assignmentId);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_RUBRIC_RESULT](state, { submissionId, assignmentId, result }) {
        let rubricModel;
        let resultModel = result;

        if (result !== NONEXISTENT) {
            if (result instanceof api.WorkRubricResultAsJSON) {
                rubricModel = Rubric.fromApiServerData(result.rubrics);
                resultModel = RubricResult.fromApiServerData(submissionId, result);
            } else if (!(result instanceof RubricResult)) {
                rubricModel = Rubric.fromServerData(result.rubrics);
                resultModel = RubricResult.fromServerData(submissionId, result);
            }
        }

        if (assignmentId != null && rubricModel != null && state.rubrics[assignmentId] == null) {
            Vue.set(state.rubrics, assignmentId, rubricModel);
        }
        Vue.set(state.results, submissionId, resultModel);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_RUBRIC_RESULT](state, { submissionId }) {
        Vue.delete(state.results, submissionId);
    },

    /** Clear the entire rubric store. */
    [types.CLEAR_RUBRIC_ALL](state) {
        Vue.set(state, 'rubrics', {});
        Vue.set(state, 'results', {});
    },
};

export default {
    namespaced: true,
    state: {
        rubrics: {},
        results: {},
    },
    getters,
    actions,
    mutations,
};

utils.signal.AFTER_USER_CHANGED.register(async () => {
    store.commit(`rubrics/${types.CLEAR_RUBRIC_ALL}`, null, { root: true });
});
